import {
  CredentialTabGroup,
  CredentialWithId,
  PortalCredentialGroup,
} from "../types/credentialTypes";

/**
 * Marks active credentials sharing the same username within a portal as duplicates.
 * A credential is flagged `isDuplicate: true` only when two or more active
 * credentials in the same portal share the exact username.
 */
const flagDuplicateCredentials = (
  credentials: CredentialWithId[],
): CredentialWithId[] => {
  const activeUsernameCounts = credentials.reduce<Record<string, number>>(
    (acc, credential) => {
      if (!credential.is_active) return acc;
      const username = credential.username;
      acc[username] = (acc[username] ?? 0) + 1;
      return acc;
    },
    {},
  );

  return credentials.map((credential) => ({
    ...credential,
    isDuplicate:
      credential.is_active &&
      (activeUsernameCounts[credential.username] ?? 0) > 1,
  }));
};

const groupCredentialsByPortalId = (
  credentials: CredentialWithId[],
): PortalCredentialGroup[] => {
  const portalMap = new Map<string, CredentialWithId[]>();

  credentials.forEach((credential) => {
    const existingCredentials = portalMap.get(credential.portal_id) ?? [];
    portalMap.set(credential.portal_id, [...existingCredentials, credential]);
  });

  return Array.from(portalMap.entries()).map(
    ([portal_id, portalCredentials]) => ({
      portal_id,
      credentials: flagDuplicateCredentials(portalCredentials),
    }),
  );
};

export const groupTabCredentialsByPortalId = (
  groupedCredentials: Array<{
    label: string;
    value: string;
    credentials: CredentialWithId[];
  }>,
): CredentialTabGroup[] =>
  groupedCredentials.map((group) => {
    const portalGroups = groupCredentialsByPortalId(group.credentials);
    const flaggedCredentials = portalGroups.flatMap((p) => p.credentials);
    return {
      label: group.label,
      value: group.value,
      credentials: flaggedCredentials,
      portalGroups,
    };
  });

export const getGroupedCredentialsByValue = (
  groupedCredentials: CredentialTabGroup[],
  value: string,
): CredentialTabGroup | undefined =>
  groupedCredentials.find((group) => group.value === value);

export const getPortalGroupsByValue = (
  groupedCredentials: CredentialTabGroup[],
  value: string,
): PortalCredentialGroup[] =>
  getGroupedCredentialsByValue(groupedCredentials, value)?.portalGroups ?? [];
